import axios from 'axios';
import { config } from '@/config';
import {
  getRoutePublicProfileFriendlyUrl,
  getRouteTenantId,
  isTenantRouteScope,
  setRouteContext,
  type AppRouteScope
} from '@/routes/routeScope';

const tenantStorageKey = 'tenantId';

export interface LoginTenantResolution {
  tenantId: string;
  isResolvedFromUrl: boolean;
  source:
    | 'api'
    | 'domain'
    | 'manual'
    | 'default'
    | (string & Record<never, never>);
  publicProfileFriendlyUrl?: string;
}

interface TenantResolutionResponse {
  tenantId?: string;
  id?: string;
  source?: string;
  friendlyUrl?: string;
  publicProfileFriendlyUrl?: string;
  scope?: AppRouteScope;
}

function normalizeTenantId(value?: string | null) {
  return value?.trim() || '';
}

export function normalizeTenantHost(host?: string | null) {
  return (host ?? '')
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\.$/, '');
}

export function getCurrentTenantHost() {
  if (typeof window === 'undefined') {
    return '';
  }

  return normalizeTenantHost(window.location.hostname);
}

export function isLocalHost(host: string) {
  return ['localhost', '127.0.0.1', '::1'].includes(host);
}

function getConfiguredAppHost() {
  if (!config.appUrl) {
    return '';
  }

  try {
    return normalizeTenantHost(new URL(config.appUrl).hostname);
  } catch {
    return normalizeTenantHost(
      config.appUrl
        .replace(/^https?:\/\//i, '')
        .split('/')[0]
        .split(':')[0]
    );
  }
}

export function isApplicationHost(host: string) {
  const appHost = getConfiguredAppHost();

  return Boolean(appHost && normalizeTenantHost(host) === appHost);
}

export function getStoredTenantId() {
  const routeTenantId = isTenantRouteScope()
    ? normalizeTenantId(getRouteTenantId())
    : '';

  if (routeTenantId) {
    return routeTenantId;
  }

  if (typeof window === 'undefined') {
    return normalizeTenantId(config.tenantId);
  }

  return (
    normalizeTenantId(window.sessionStorage.getItem(tenantStorageKey)) ||
    normalizeTenantId(config.tenantId)
  );
}

export function setStoredTenantId(tenantId: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(tenantStorageKey, normalizeTenantId(tenantId));
}

export function clearStoredTenantId() {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(tenantStorageKey);
}

export function getTenantHostHeaders() {
  const host = getCurrentTenantHost();

  return host ? { 'x-hsdesk-host': host } : {};
}

export function getTenantRequestHeaders() {
  return {
    tenant: getStoredTenantId(),
    ...getTenantHostHeaders()
  };
}

export async function resolveTenantByHost(
  host: string
): Promise<LoginTenantResolution | null> {
  const normalizedHost = normalizeTenantHost(host);

  if (
    !normalizedHost ||
    isLocalHost(normalizedHost) ||
    isApplicationHost(normalizedHost)
  ) {
    return null;
  }

  const response = await axios.get<TenantResolutionResponse>(
    `${config.apiUrl}/api/v1/tenants/resolve`,
    {
      params: { host: normalizedHost },
      headers: { 'x-hsdesk-host': normalizedHost },
      timeout: 1500
    }
  );
  const tenantId = normalizeTenantId(
    response.data.tenantId || response.data.id
  );

  if (!tenantId) {
    return null;
  }

  setStoredTenantId(tenantId);

  setRouteContext({
    scope: 'tenant',
    tenantId,
    publicProfileFriendlyUrl:
      normalizeTenantId(
        response.data.publicProfileFriendlyUrl || response.data.friendlyUrl
      ) || undefined
  });

  return {
    tenantId,
    isResolvedFromUrl: true,
    source: response.data.source || 'api',
    publicProfileFriendlyUrl:
      normalizeTenantId(
        response.data.publicProfileFriendlyUrl || response.data.friendlyUrl
      ) || undefined
  };
}

export async function resolveLoginTenant(): Promise<LoginTenantResolution> {
  const host = getCurrentTenantHost();

  if (!host || isLocalHost(host)) {
    return {
      tenantId: getStoredTenantId(),
      isResolvedFromUrl: false,
      source: 'manual'
    };
  }

  const routeTenantId = normalizeTenantId(getRouteTenantId());

  if (isTenantRouteScope() && routeTenantId) {
    return {
      tenantId: routeTenantId,
      isResolvedFromUrl: true,
      source: 'domain',
      publicProfileFriendlyUrl: getRoutePublicProfileFriendlyUrl()
    };
  }

  try {
    const resolvedTenant = await resolveTenantByHost(host);

    if (resolvedTenant) {
      return resolvedTenant;
    }
  } catch {
    // Backend resolver is optional until the API project exposes it.
  }

  return {
    tenantId: getStoredTenantId(),
    isResolvedFromUrl: false,
    source: 'default'
  };
}
